import {
  Component,
  PLATFORM_ID,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { EventService, SettingService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { filter } from 'rxjs/operators';
import { PopupBannerComponent } from '../../components/popup-banner/popup-banner.component';
import FloatingButtonsComponent from '../../components/floating-buttons/floating-buttons.component';
import { NAV_ITEMS } from '../../const/nav-items.const';
import FooterComponent from '../footer/footer/footer.component';
import HeaderComponent from '../header/header.component';
import { ScreenSizeStore } from '../../stores/screen-size.store';

@Component({
  selector: 'app-client-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    Icon,
    FloatingButtonsComponent,
    PopupBannerComponent,
  ],
  templateUrl: './client-layout.component.html',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateX(100%)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate(
          '400ms ease-out',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateY(-100%)', opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export default class ClientLayoutComponent implements OnInit {
  private readonly eventService = inject(EventService);
  private readonly router = inject<Router>(Router);
  private readonly settingService = inject(SettingService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly popupStorageKey = 'event_popup_hidden';
  private readonly screenSizeStore = inject(ScreenSizeStore);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  readonly logoData = toSignal(
    this.settingService.settingControllerFindByKey({
      key: 'millionshow_logo_url',
    })
  );

  readonly menuOpen = signal(false);
  readonly eventPopupVisible = signal(false);
  readonly isHomePage = signal(false);
  readonly navItems = NAV_ITEMS;
  readonly isMobileSize = this.screenSizeStore.isMobileSize;
  readonly isFloatingHiddenPage = signal(false);

  readonly relatedSites = [
    { label: '마이스좋은공간', url: 'https://xn--s39awr644d2fb.kr/' },
    { label: '건축박람회', url: 'https://www.allhouse.or.kr/' },
    { label: '명절선물박람회', url: 'http://www.giftfair.co.kr/' },
    { label: '농기자재전시회', url: 'https://www.agritex.kr/' },
    { label: '창업&프랜차이즈', url: 'https://www.startfair.net/' },
  ];

  readonly $events = rxResource({
    stream: () =>
      this.eventService.eventControllerFindAllV1({
        status: 'ONGOING',
        type: 'MILLIONSHOW' as const,
      }),
  });

  readonly upcomingEvent = computed(() => {
    const items = this.$events.value() || [];
    if (items.length === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingItems = items.filter(event => {
      const startDate = new Date(event.startAt);
      startDate.setHours(0, 0, 0, 0);

      const diffTime = startDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 1;
    });

    if (upcomingItems.length === 0) return null;

    const sorted = [...upcomingItems].sort(
      (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
    );

    return sorted[0];
  });

  readonly dDay = computed(() => {
    const event = this.upcomingEvent();
    if (!event) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(event.startAt);
    startDate.setHours(0, 0, 0, 0);

    const diffTime = startDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'D-Day';
    if (diffDays > 0) return `D-${diffDays}`;
    return `D+${Math.abs(diffDays)}`;
  });

  ngOnInit(): void {
    this.checkEventPopup();
    this.updatePageStatus();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updatePageStatus();
      });
  }

  updatePageStatus(): void {
    const url = this.router.url;
    this.isHomePage.set(url === '/' || url === '');

    const isPolicy = url.includes('/policy');
    const isEventDetail =
      url.startsWith('/event/') && url.split('/').length > 2;
    const isRegistration = url.includes('/registration-form');
    const isBoothApp = url.includes('/booth-application-form');

    this.isFloatingHiddenPage.set(
      isPolicy || isEventDetail || isRegistration || isBoothApp
    );
  }

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());

    if (!this.isBrowser) {
      return;
    }

    document.body.style.overflow = this.menuOpen() ? 'hidden' : '';
  }

  checkEventPopup(): void {
    if (!this.isBrowser) {
      this.eventPopupVisible.set(false);
      return;
    }

    const hiddenUntil = localStorage.getItem(this.popupStorageKey);

    if (hiddenUntil) {
      const hiddenDate = new Date(hiddenUntil);
      const now = new Date();

      if (now < hiddenDate) {
        this.eventPopupVisible.set(false);
        return;
      }

      localStorage.removeItem(this.popupStorageKey);
    }

    this.eventPopupVisible.set(true);
  }

  closeEventPopup(): void {
    if (!this.isBrowser) {
      return;
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    localStorage.setItem(this.popupStorageKey, tomorrow.toISOString());
    this.eventPopupVisible.set(false);
  }
}
